<template>
  <div class="bc-edit__wrapper">
    <system-card
      v-if="systemMessage"
      :system-message="systemMessage"
      :cb-func="loadCard"
    />
    <up-card
      v-if="textFields.length"
      class="bc-card"
    >
      <h2 class="bc-card__title">{{ $t('businessCardEdit.personal_data') }}</h2>
      <div
        v-for="field in textFields"
        :key="field.key"
        class="row"
      >
        <div class="col-12">
          <div
            v-if="isReadonly(field)"
            class="bc-edit__readonly"
          >
            <span class="bc-edit__readonly-label">{{ fieldLabel(field) }}</span>
            <p class="bc-edit__readonly-value">
              <i
                v-if="field.icon"
                :class="field.icon"
                class="bc-edit__readonly-icon"
              />
              <span>{{ card.values[field.key] }}</span>
            </p>
          </div>
          <input-field
            v-else
            v-model="card.values[field.key]"
            :label="fieldLabel(field)"
            :icon-left="field.icon"
          />
        </div>
      </div>
    </up-card>
    <up-card
      v-if="listFields.length"
      class="bc-card"
    >
      <h2 class="bc-card__title">{{ $t('businessCardEdit.contacts') }}</h2>
      <div
        v-for="field in listFields"
        :key="field.key"
        class="row"
      >
        <div
          v-if="isReadonly(field)"
          class="col-12"
        >
          <div class="bc-edit__readonly">
            <span class="bc-edit__readonly-label">{{ fieldLabel(field) }}</span>
            <p
              v-for="(value, index) in filledValues(field)"
              :key="`${field.key}-${index}`"
              class="bc-edit__readonly-value"
            >
              <i
                v-if="field.icon"
                :class="field.icon"
                class="bc-edit__readonly-icon"
              />
              <span>{{ value }}</span>
            </p>
          </div>
        </div>
        <template v-else>
          <div
            v-for="(value, index) in card.values[field.key]"
            :key="`${field.key}-${index}`"
            class="col-12"
          >
            <div class="bc-edit__field-row">
              <component
                :is="field.component"
                v-model="card.values[field.key][index]"
                :label="index === 0 ? fieldLabel(field) : ''"
                :icon-left="field.icon"
              />
              <button
                v-if="card.values[field.key].length > 1"
                class="bc-card__btn-delete"
                @click="removeItem(field, index)"
              >
                <i class="exu-x-close" />
              </button>
            </div>
          </div>
          <div class="col-12">
            <button
              class="bc-card__btn-add"
              @click="addItem(field)"
            >{{ $t(field.addLabelKey) }}</button>
          </div>
        </template>
      </div>
    </up-card>
    <up-card
      v-for="field in card.extendFields"
      :key="field.name"
      class="bc-card"
    >
      <h2 class="bc-card__title bc-card__title--question">{{ field.label }}</h2>
      <checkbox-list
        v-model="card.extendValues[field.name]"
        :options="field.options"
        :label="field.label"
      />
    </up-card>
    <div class="row">
      <div class="col-4">
        <up-button
          class="up-btn bc-card__btn btn-decline"
          @click="$router.back()"
        >{{ $t('shared.form.btn_cancel') }}</up-button>
      </div>
      <div class="col-8">
        <up-button
          class="up-btn bc-card__btn btn-success"
          :disabled="isSaving"
          @click="handleSave"
        >
          <span v-if="isSaving" class="bc-card__btn-spinner" />
          <span v-else>{{ $t('businessCardEdit.btn_save') }}</span>
        </up-button>
      </div>
    </div>
    <p v-if="saveError" class="bc-save-error">{{ saveError }}</p>
  </div>
</template>

<script>
import appBaseNav from '@/components/mixins/appBaseNav';
import businessCard from '@/components/mixins/methodsBusinessCard';
import SystemCard from '@/components/shared/upCard/SystemCard.vue';
import { CheckboxList } from '@/components/molecules';
import { emptyCard, parseCard, toPayload } from '@/components/mixins/businessCardFields';

export default {
  name: 'BusinessCardEdit',
  components: { SystemCard, CheckboxList },
  props: {
    locale: { type: String },
  },
  data() {
    return {
      isCreated: false,
      isLoading: true,
      isSaving: false,
      systemMessage: null,
      saveError: null,
      // состав полей приходит с сервера — до загрузки форма пустая
      card: emptyCard(),
      // откуда пришли в форму — от этого зависит переход после сохранения
      returnBack: false,
      fromExchange: false,
    };
  },
  computed: {
    // Поле с readonly показываем строкой, а пустое readonly-поле не показываем
    // вовсе: править его тут нельзя, и выводить нечего.
    textFields() {
      return this.card.textFields.filter((field) => this.isVisible(field));
    },
    listFields() {
      return this.card.listFields.filter((field) => this.isVisible(field));
    },
  },
  mixins: [appBaseNav, businessCard],
  beforeRouteEnter(to, from, next) {
    // Экраны, на которые после сохранения надо вернуться, а не подставить
    // поверх «Мою визитку». С предпросмотра — чтобы «назад» не приводил на ту
    // же страницу. Из профиля участника — потому что человек пришёл оттуда
    // обмениваться и на своей визитке ему делать нечего.
    const BACK_FROM = ['businessCardPreview', 'participantProfile', 'businessCardCollection'];
    // Сюда уводит незавершённый обмен: своей визитки не было, и подтверждение
    // не отправилось. После сохранения об этом надо сказать — человек к тому
    // времени уже не помнит, на чём остановился.
    const UNFINISHED_EXCHANGE = ['participantProfile', 'businessCardCollection'];
    next((vm) => {
      // eslint-disable-next-line no-param-reassign
      vm.returnBack = BACK_FROM.includes(from.name);
      // eslint-disable-next-line no-param-reassign
      vm.fromExchange = UNFINISHED_EXCHANGE.includes(from.name);
    });
  },
  watch: {
    locale() {
      this.$store.commit('setPageTitle', this.$t('businessCardEdit.header_title'));
    },
  },
  mounted() {
    this.$store.commit('setPageTitle', this.$t('businessCardEdit.header_title'));
    this.loadCard();
  },
  methods: {
    loadCard() {
      this.isLoading = true;
      this.systemMessage = null;
      this.getMyBusinessCard()
        .then((data) => {
          this.isLoading = false;
          this.isCreated = data.is_created;
          // описатели дополнительных полей лежат рядом с data, а не внутри неё
          const parsed = parseCard(data.data, data.extend_data, this.$store.getters.getLang);
          if (!parsed) {
            this.systemMessage = this.$t('system.bad_connect');
            return;
          }
          this.card = parsed;
        })
        .catch(() => {
          this.isLoading = false;
          this.systemMessage = this.$t('system.bad_connect');
        });
    },
    fieldLabel(field) {
      return field.label || this.$t(field.labelKey);
    },
    isReadonly(field) {
      return !!this.card.readonly[field.key];
    },
    filledValues(field) {
      return (this.card.values[field.key] || []).filter((v) => v);
    },
    isVisible(field) {
      if (!this.isReadonly(field)) return true;
      const value = this.card.values[field.key];
      return Array.isArray(value) ? value.some((v) => v) : !!value;
    },
    handleSave() {
      this.isSaving = true;
      this.saveError = null;
      this.saveBusinessCard(toPayload(this.card))
        .then(() => {
          this.isSaving = false;
          // Форма не должна оставаться в истории: кнопка «назад» на «Моей визитке»
          // возвращает туда, откуда пользователь вошёл в редактирование.
          // Тост кладём до перехода: он живёт в сторе и переезд переживает,
          // а прочитан будет уже на том экране, где обмен и остался незакрытым.
          if (this.fromExchange) {
            this.$store.commit('pushToast', {
              text: this.$t('participantProfile.exchange.toast_card_ready'),
            });
          }
          if (this.returnBack) this.$router.back();
          else this.$router.replace({ name: 'businessCardPreview' });
        })
        .catch(() => {
          this.isSaving = false;
          this.saveError = this.$t('system.bad_connect');
        });
    },
    addItem(field) {
      this.card.values[field.key].push('');
    },
    removeItem(field, index) {
      this.card.values[field.key].splice(index, 1);
    },
    handleDelete() {
      this.deleteBusinessCard()
        .then(() => {
          this.isCreated = false;
          this.card = emptyCard();
        })
        .catch(() => {});
    },
    handlePreview() {
      this.$router.push({ name: 'businessCardPreview' });
    },
  },
};
</script>

<style lang="scss" scoped>
.bc-edit {
  &__wrapper {
    display: flex;
    flex-direction: column;
    max-width: 760px;
    margin: 0 auto;
    padding: 12px;
    gap: 12px;
  }

  &__field-row {
    display: flex;
    align-items: flex-end;
    gap: 8px;

    .input-field {
      flex: 1;
    }
  }

  // поле с readonly: подпись и значение вместо поля ввода
  &__readonly {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__readonly-label {
    font-size: 14px;
    color: #6b7280;
  }

  &__readonly-value {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    min-height: 24px;
    font-size: 15px;
    color: #1f1f1f;
  }

  &__readonly-icon {
    flex-shrink: 0;
    font-size: 20px;
    color: #9e9e9e;
  }
}
.bc-card {
  border-radius: 14px;

  & > div {
    gap: 17px;
  }

  & .row {
    gap: 6px;
  }
}
.bc-card__title {
  border-radius: 14px;
  margin: 0;
}
.bc-card__title {
  text-transform: uppercase;
  font-size: 14px;
  color: 1f4f7f;
}
// Заголовок дополнительного поля — это вопрос организатора целой строкой,
// капслоком он читается как крик.
.bc-card__title--question {
  text-transform: none;
  font-size: 15px;
  line-height: 1.35;
  color: #1c1c1c;
}
.bc-card__btn-add, .bc-card__btn-delete {
  border: none;
  background: none;
  font-size: 13px;
}
.bc-card__btn-add {
  color: #1c1c1c;
}
.bc-card__btn-delete {
  display: flex;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  color: #1c1c1c;
  font-size: 24px;
  padding: 0;
}
.bc-card__btn {
  height: 48px;
  border-radius: 14px;

  &.btn-decline {
    border-color: #1c1c1c;
    color: #1c1c1c;
  }
  &.btn-success {
    border-color: #ff957d;
    color: #ff957d;
  }
}
.bc-card__btn-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: bc-spin 0.7s linear infinite;
}
@keyframes bc-spin {
  to { transform: rotate(360deg); }
}
.bc-save-error {
  margin: 0;
  padding: 0 12px;
  font-size: 13px;
  color: #c0392b;
  text-align: center;
}
</style>
